// DiscographyServices.js
import { useState, useCallback } from "react";
import API from "../../api/apiAdmin";
import { useNotification } from "../../context/NotificationContext";

export function useDiscography() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { showNotification } = useNotification();

    const fetchAllMusic = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.get("/admin/discography/");
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to fetch music.";
            setError(msg);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchMusicById = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.get(`/admin/discography/${id}`);
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to fetch music details.";
            setError(msg);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const createMusic = useCallback(async (formData) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.post("/admin/discography/", formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            if (res.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Music Created",
                    message: res.data.message,
                });
            }
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to create music.";
            setError(msg);
            showNotification({
                type: "failed",
                title: "Error",
                message: msg,
            });
            return { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    const updateMusic = useCallback(async (id, formData) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.put(`/admin/discography/${id}`, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            if (res.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Music Updated",
                    message: res.data.message,
                });
            }
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to update music.";
            setError(msg);
            showNotification({
                type: "failed",
                title: "Error",
                message: msg,
            });
            return { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    const deleteMusic = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.delete(`/admin/discography/${id}`);
            if (res.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Music Deleted",
                    message: res.data.message,
                });
            }
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to delete music.";
            setError(msg);
            showNotification({
                type: "failed",
                title: "Error",
                message: msg,
            });
            return { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    const toggleMusicStatus = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.patch(`/admin/discography/${id}/toggle-status`);
            if (res.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Status Updated",
                    message: res.data.message,
                });
            }
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to toggle status.";
            setError(msg);
            showNotification({
                type: "failed",
                title: "Error",
                message: msg,
            });
            return { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    return {
        loading,
        error,
        fetchAllMusic,
        fetchMusicById,
        createMusic,
        updateMusic,
        deleteMusic,
        toggleMusicStatus
    };
}
